import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { extractYouTubeId, extractVimeoId, detectVideoType } from '../services/landingContentService';
import './Pages.css';

export const ReceivedContent: React.FC = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'video' | 'card' | 'article'>('all');

  useEffect(() => {
    if (!user?.uid) return;

    const contentRef = ref(database, 'contentForNutritionists');
    const unsubscribe = onValue(contentRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const now = new Date();
        const contentArray = Object.entries(data)
          .map(([id, contentData]: [string, any]) => ({
            id,
            ...contentData
          }))
          .filter((item: any) => {
            // Verificar se o conteúdo foi enviado para este nutricionista
            if (!item.sentTo?.includes(user.uid)) {
              return false;
            }
            // Se não tem data planejada, mostrar imediatamente
            if (!item.scheduledDate) {
              return true;
            }
            // Se tem data planejada, só mostrar se a data já passou
            const scheduledDate = new Date(item.scheduledDate);
            return scheduledDate <= now;
          })
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setContent(contentArray);
      } else {
        setContent([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const markAsRead = async (contentId: string) => {
    if (!user?.uid) return;
    try {
      const notificationRef = ref(database, `notifications`);
      // Marcar notificações relacionadas como lidas
      // Implementação simplificada - pode ser melhorada
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    }
  };

  const filteredContent = filter === 'all' 
    ? content 
    : content.filter((item: any) => item.type === filter);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'video':
        return '🎥 Vídeo';
      case 'card':
        return '🃏 Card';
      case 'article':
        return '📄 Artigo';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="ns-page" style={{ textAlign: 'center', padding: '4rem' }}>
        Carregando conteúdo...
      </div>
    );
  }

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Conteúdo Recebido</h2>
          <p>Vídeos, cards e artigos científicos enviados para você</p>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        {(['all', 'video', 'card', 'article'] as const).map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            style={{
              padding: '0.5rem 1rem',
              background: filter === filterType 
                ? (isDark ? 'rgba(42, 157, 143, 0.2)' : '#d1fae5')
                : (isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9'),
              color: filter === filterType
                ? (isDark ? '#2a9d8f' : '#065f46')
                : (isDark ? '#94a3b8' : '#64748b'),
              border: `1px solid ${filter === filterType 
                ? (isDark ? '#2a9d8f' : '#10b981')
                : (isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')}`,
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: filter === filterType ? 600 : 400,
              fontSize: '0.9rem'
            }}
          >
            {filterType === 'all' && '📦 Todos'}
            {filterType === 'video' && '🎥 Vídeos'}
            {filterType === 'card' && '🃏 Cards'}
            {filterType === 'article' && '📄 Artigos'}
          </button>
        ))}
      </div>

      {/* Lista de Conteúdo */}
      {filteredContent.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          borderRadius: '12px',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <h3 style={{ color: isDark ? '#e2e8f0' : '#1e293b', marginBottom: '0.5rem' }}>
            Nenhum conteúdo encontrado
          </h3>
          <p style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            {filter === 'all' 
              ? 'Você ainda não recebeu nenhum conteúdo.'
              : `Você ainda não recebeu nenhum ${filter === 'video' ? 'vídeo' : filter === 'card' ? 'card' : 'artigo'}.`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {filteredContent.map((item: any) => {
            const videoType = item.type === 'video' ? detectVideoType(item.url || '') : null;
            const youtubeId = item.url ? extractYouTubeId(item.url) : null;
            const vimeoId = item.url ? extractVimeoId(item.url) : null;

            return (
              <div
                key={item.id}
                style={{
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = isDark 
                    ? '0 10px 25px rgba(0, 0, 0, 0.3)'
                    : '0 10px 25px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: isDark ? 'rgba(42, 157, 143, 0.2)' : '#d1fae5',
                    color: isDark ? '#2a9d8f' : '#065f46',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }}>
                    {getTypeLabel(item.type)}
                  </span>
                  <h3 style={{ margin: 0, color: isDark ? '#e2e8f0' : '#1e293b', flex: 1 }}>
                    {item.title}
                  </h3>
                  <span style={{
                    fontSize: '0.85rem',
                    color: isDark ? '#94a3b8' : '#64748b'
                  }}>
                    {formatDate(item.createdAt)}
                  </span>
                </div>

                {/* Vídeo */}
                {item.type === 'video' && item.url && (
                  <div style={{ marginBottom: '1rem', width: '100%' }}>
                    <div style={{
                      position: 'relative',
                      paddingBottom: '56.25%',
                      height: 0,
                      overflow: 'hidden',
                      borderRadius: '8px',
                      background: isDark ? 'rgba(0, 0, 0, 0.3)' : '#f1f5f9',
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      {videoType === 'youtube' && youtubeId ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px',
                            minWidth: '100%',
                            minHeight: '100%'
                          }}
                        />
                      ) : videoType === 'vimeo' && vimeoId ? (
                        <iframe
                          src={`https://player.vimeo.com/video/${vimeoId}`}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px',
                            minWidth: '100%',
                            minHeight: '100%'
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            minWidth: '100%',
                            minHeight: '100%'
                          }}
                          src={item.url}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Conteúdo do Card ou Artigo */}
                {(item.type === 'card' || item.type === 'article') && item.content && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '1.5rem',
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(42, 157, 143, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)' 
                      : 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%)',
                    borderRadius: '12px',
                    border: `1px solid ${isDark ? 'rgba(42, 157, 143, 0.2)' : '#d1fae5'}`,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    lineHeight: 1.8,
                    fontSize: '1rem',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    boxShadow: isDark 
                      ? '0 2px 8px rgba(0, 0, 0, 0.2)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    position: 'relative'
                  }}>
                    {/* Ícone decorativo */}
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      fontSize: '2rem',
                      opacity: 0.1
                    }}>
                      {item.type === 'card' ? '🃏' : '📄'}
                    </div>
                    <div style={{
                      position: 'relative',
                      zIndex: 1,
                      maxWidth: '100%'
                    }}>
                      {typeof item.content === 'string' ? (
                        <div 
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word'
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: item.content.replace(/\n/g, '<br />') 
                          }}
                        />
                      ) : (
                        <div>{item.content}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Imagem do Card se houver */}
                {item.type === 'card' && item.imageUrl && (
                  <div style={{
                    marginBottom: '1rem',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: isDark 
                      ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                      : '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img 
                      src={item.imageUrl} 
                      alt={item.title || 'Card image'}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxHeight: '500px',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* PDF Link */}
                {item.type === 'article' && item.pdfUrl && (
                  <div style={{ marginTop: '1rem' }}>
                    <a
                      href={item.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: isDark ? 'rgba(42, 157, 143, 0.2)' : '#2a9d8f',
                        color: isDark ? '#2a9d8f' : '#ffffff',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDark ? 'rgba(42, 157, 143, 0.3)' : '#1f776c';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isDark ? 'rgba(42, 157, 143, 0.2)' : '#2a9d8f';
                      }}
                    >
                      📄 Ver Artigo Completo (PDF)
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};




